import { reducer as formReducer } from 'redux-form';
import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';
import moment from 'moment';

import { COUNTRY_CZ } from '../enums/countries';
import { IDCARD_TYPE_ID } from "../enums/GuestDetails";
import StayType from '../enums/StayType';

import {
	SET_INITIATED,
	SET_STEP,
	SET_MAX_STEP,
	SET_LOADING,
	SET_CAN_CONTINUE,
	SET_ROOM_OFFERS,
	SET_API_ROOM_TYPES_AVAILABILITIES_RESPONSE,
	SET_API_ROOMS_RESPONSE,
	SET_API_DATA_RESPONSE,
	SET_STORNO_INSURANCE_PRICE,
	SET_TOTAL_PRICE,
	SET_PRICE_TO_PAY,
	SET_SELECTED_ROOM_TYPE,
	SET_RESERVATION_STATUS,
	SET_TOTAL_NO_OF_GUESTS,
	SET_NO_OF_NIGHTS,
	SET_LANG,
	SET_TRANSLATIONS,
	SET_API_DISCOUNT_CODE_RESPONSE,
	SET_DISCOUNT_CODE_AUTO_REMOVED,
	SET_GOOGLE_SOLD_OUT,
} from './actions';

import { DATE_FORMAT } from '../constants';

const appActionHandlers = {
	[SET_INITIATED]: (state, action) => ({
		...state,
		initiated: action.payload,
	}),
	[SET_STEP]: (state, action) => {
		const newState = {
			...state,
			step: action.payload,
		};

		if (newState.step > newState.maxStep) {
			newState.maxStep = newState.step;
		}

		return newState;
	},
	[SET_MAX_STEP]: (state, action) => ({
		...state,
		setMaxStep: action.payload,
	}),
	[SET_LANG]: (state, action) => ({
		...state,
		lang: action.payload,
	}),
	[SET_LOADING]: (state, action) => ({
		...state,
		isLoading: action.payload,
	}),
	[SET_CAN_CONTINUE]: (state, action) => ({
		...state,
		canContinue: action.payload,
	}),
	[SET_ROOM_OFFERS]: (state, action) => ({
		...state,
		roomOffers: action.payload,
	}),
	[SET_API_ROOMS_RESPONSE]: (state, action) => ({
		...state,
		apiRoomsResponse: action.payload,
	}),
	[SET_API_ROOM_TYPES_AVAILABILITIES_RESPONSE]: (state, action) => ({
		...state,
		apiRoomTypesAvailabilitiesResponse: action.payload,
	}),
	[SET_API_DATA_RESPONSE]: (state, action) => {
		const apiDataResponse = action.payload;
		delete apiDataResponse.css;
		const newState = Object.assign(state, apiDataResponse);
		// Nastavíme výchozí počet hostů dle priceDisplayGuestCount, pokud nebyl zadán přes URL
		if (!newState.guestsSetByUser && newState.priceDisplayGuestCount) {
			newState.guests = newState.priceDisplayGuestCount;
		}
		if (newState.package) {
			newState.packages = { ...newState.packages, [newState.package.id]: newState.package };
		}
		return newState;
	},
	[SET_API_DISCOUNT_CODE_RESPONSE]: (state, action) => ({
		...state,
		apiDiscountCodeResponse: action.payload,
		discountCodeAutoRemoved: false,
	}),
	[SET_DISCOUNT_CODE_AUTO_REMOVED]: (state, action) => ({
		...state,
		discountCodeAutoRemoved: action.payload,
	}),
	[SET_STORNO_INSURANCE_PRICE]: (state, action) => ({
		...state,
		stornoInsurancePrice: action.payload,
	}),
	[SET_TOTAL_PRICE]: (state, action) => ({
		...state,
		totalPrice: action.payload,
	}),
	[SET_PRICE_TO_PAY]: (state, action) => ({
		...state,
		priceToPay: action.payload,
	}),
	[SET_SELECTED_ROOM_TYPE]: (state, action) => ({
		...state,
		selectedRoomType: action.payload,
	}),
	[SET_RESERVATION_STATUS]: (state, action) => ({
		...state,
		reservationStatus: action.payload,
	}),
	[SET_TOTAL_NO_OF_GUESTS]: (state, action) => ({
		...state,
		totalNoOfGuests: action.payload,
	}),
	[SET_NO_OF_NIGHTS]: (state, action) => ({
		...state,
		noOfNights: action.payload,
	}),
	[SET_TRANSLATIONS]: (state, action) => ({
		...state,
		translations: {
			...state.translations,
			[action.payload.lang]: action.payload.translations,
		}
	}),
	[SET_GOOGLE_SOLD_OUT]: (state, action) => ({
		...state,
		googleSoldOut: action.payload,
	}),
};

const appDefaultInitialValues = {
	canContinue: true,
	apiOrigin: 'https://res.plazaro.loc',
	// apiOrigin: 'https://res.plazaro-dev.com',
	hotelId: 'clyxut',
	// hotelId: 'ad8vei',
	lang: 'cs',
	theme: 'system',
	// Pokud je 1, tak zobrazovat všechny pokoje (i ty obsazené)
	all: 0,
	// Používá se především jako query parametr ?selectedRoomType=<room_type_id> pro zobrazení konkreního typu pokoje
	selectedRoomType: null, // více hodnot je možné oddělit čárkou
	guests: 1, // výchozí počet hostů na pokoji
	step: 1,
	maxStep: 1,
	apiRoomsResponse: null,
	isLoading: true,
	roomOffers: [],
	stornoInsurancePrice: 0,
	totalPrice: 0,
	priceToPay: 0,
	totalNoOfGuests: 0,
	noOfNights: 1,
	// Pokoj, který host vybral v Googlu, není volný
	googleSoldOut: false,
	// vyska fixniho menu na strance (pri vlozeni do iframu, kvuli scrollovani nahoru)
	fixedHeaderHeight: 0,
	reservationStatus: null,
	initiated: false,
};

export const bookingFormInitialValues = {
	arrival: moment().format(DATE_FORMAT),
	departure: moment().add(1, 'day').format(DATE_FORMAT),
	rooms: {},

	// Typ pobytu
	stayType: StayType.VACATION,

	// Strava
	boardBase: null,

	// Omezení stravy
	dietRestriction: [],

	firstName: process.env.NODE_ENV === 'development' ? 'Adam' : '',
	lastName: process.env.NODE_ENV === 'development' ? 'Štěpánek' : '',
	phone: process.env.NODE_ENV === 'development' ? '+420775112448' : '+420',
	email: process.env.NODE_ENV === 'development' ? 'adam@appsdevteam.com' : '',
	street: process.env.NODE_ENV === 'development' ? 'Křížkovského 33a' : '',
	city: process.env.NODE_ENV === 'development' ? 'Brno' : '',
	postCode: process.env.NODE_ENV === 'development' ? '60300' : '',

	isCompany: false,
	// Česká republika
	companyCountry: COUNTRY_CZ,

	guestDetails: process.env.NODE_ENV === 'development' ? [
		{
			firstname: 'Jan',
			surname: 'Novák',
			sex: 'male',
			birth_date: moment().add(-30, 'year').format(DATE_FORMAT),
			citizenship_id: COUNTRY_CZ,
			idcard_type_id: IDCARD_TYPE_ID,
			idcard_number: '123456789',
			street: 'Křížkovského 33a',
			city: 'Brno',
			zip: '60300',
			country_id: COUNTRY_CZ,
			signature: 'data:image/svg+xml;base64,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',
		},
		{
			firstname: 'Jana',
			surname: 'Nováková',
			sex: 'female',
			birth_date: moment().add(-30, 'year').format(DATE_FORMAT),
			citizenship_id: COUNTRY_CZ,
			idcard_type_id: IDCARD_TYPE_ID,
			idcard_number: '123456789',
			street: 'Křížkovského 33a',
			city: 'Brno',
			zip: '60300',
			country_id: COUNTRY_CZ,
		},
		{
			firstname: 'Michal',
			surname: 'Novák',
			sex: 'male',
			birth_date: moment().add(-18, 'year').format(DATE_FORMAT),
			citizenship_id: COUNTRY_CZ,
			idcard_type_id: IDCARD_TYPE_ID,
			idcard_number: '123456789',
			street: 'Křížkovského 33a',
			city: 'Brno',
			zip: '60300',
			country_id: COUNTRY_CZ,
		},
	] : [],

	paymentMethod: null,
	advancePaymentOnly: false,

	stornoInsurance: false,

	lang: 'cs',
};

const query = new URLSearchParams(window.location.search);

const arrival = query.get('arrival') ? moment(query.get('arrival'), 'YYYY-MM-DD') : null;
const departure = query.get('departure') ? moment(query.get('departure'), 'YYYY-MM-DD') : null;

if (arrival) {
	bookingFormInitialValues.arrival = arrival.format(DATE_FORMAT);
	bookingFormInitialValues.departure = departure
		? departure.format(DATE_FORMAT)
		: arrival.add(1, 'day').format(DATE_FORMAT);
} else if (departure) {
	bookingFormInitialValues.departure = departure.format(DATE_FORMAT);
}

export default function(initialValues) {

	if (initialValues.step) {
		initialValues.step = parseInt(initialValues.step, 10);
	}

	initialValues.guestsSetByUser = !!initialValues.guests;
	if (initialValues.guests) {
		initialValues.guests = parseInt(initialValues.guests, 10);
		if (isNaN(initialValues.guests) || initialValues.guests < 1) {
			initialValues.guests = 1;
		}
	}

	if (initialValues.selectedRoomType !== undefined) {
		initialValues.selectedRoomType = initialValues.selectedRoomType.split(',').map(val => parseInt(val, 10)).filter(val => !isNaN(val));
		if (initialValues.selectedRoomType.length === 0) {
			initialValues.selectedRoomType = null;
		}
	}

	const appInitialValues = Object.assign(appDefaultInitialValues, initialValues);

	return combineReducers({
		app: handleActions(appActionHandlers, appInitialValues),
		form: formReducer
	});
}



// WEBPACK FOOTER //
// ./src/store/reducers.js